import i18n from '@/i18n';

const t = i18n.t.bind(i18n);

/**
 * 菜单标题 / 描述映射（静态中文）。
 * 键须与 useMenu、auth-conf、权限配置中的 title 保持一致。
 */

/** 带标题与可选描述的菜单节点 */
export interface NavMapMenuEntry {
  title: string;
  description?: string;
}

/** 菜单穿梭框等使用的纯文案键 */
export interface NavMapUiStrings {
  selected: string;
  source: string;
  toRight: string;
  toLeft: string;
  functionNav: string;
}

/** 复用描述，避免多处拷贝导致文案不一致 */
const DESC_ALERT_HISTORY = i18n.t('hooks:tracking.and.traceability.query.of.historical.alert.events');
const DESC_NOTIFY_CHANNELS = i18n.t('hooks:supports.configuration.of.multiple.notification.channels.such.as.email.dingtalk.and.webhook');
const DESC_BILL_ACCOUNTING_SYSTEM = t(
  'hooks:build.a.unified.accurate.transparent.and.scalable.billing.management.system.which.aims.to.clearly.record.calculate.and.display.the.fees.incurred.by.users.for.consuming.various.intelligent.computing.resources.and.provide.efficient.billing.reconciliation.and.financial.analysis.capabilities',
);
const DESC_ORDER_WORKBENCH = t(
  'hooks:a.comprehensive.workbench.integrating.information.display.status.filtering.quick.search.and.batch.operations.serving.as.a.key.entry.point.for.efficient.order.processing.workflows',
);
const DESC_VM_COMPUTE = t(
  'hooks:provides.standard.computing.resources.based.on.virtualization.technology.supporting.diverse.computing.needs.from.general.applications.to.high.performance.gpu.acceleration.scenarios.with.good.isolation.and.elastic.scaling.capabilities',
);
const DESC_K8S_CONTAINER_PLATFORM = t(
  'hooks:container.runtime.and.scheduling.platform.built.on.kubernetes.supporting.lightweight.service.deployment.and.high.computing.power.container.workloads.adapted.for.microservice.architectures.and.elastic.computing.needs.in.ai.scenarios',
);
const DESC_RBAC_POLICY = i18n.t('hooks:fine.grained.role.based.access.control.configuration.and.policy.distribution');

const common: NavMapUiStrings = {
  selected: t('hooks:selected.menu'),
  source: t('hooks:all.menus'),
  toRight: t('common:add'),
  toLeft: t('common:remove'),
  functionNav: t('common:feature.navigation'),
};

export const NAV_MAP = {
  ...common,
  platformManagement: {
    title: t('common:platform.management'),
    description: t(
      'hooks:the.stack.platform.management.module.provides.system.administrators.and.operation.personnel.with.comprehensive.infrastructure.and.resource.management.capabilities',
    ),
  },
  operations: {
    title: t('hooks:operations.center'),
    description: t('hooks:platform.operations.center.feature.collection'),
  },
  opsOverview: {
    title: t('hooks:overview'),
    description: t('hooks:summary.display.of.system.alerts.operation.status.and.trends'),
  },
  dashboard: {
    title: t('hooks:smart.dashboard'),
    description: t('hooks:multi.dimensional.summary.of.platform.key.metrics.and.events.supporting.visualization.dashboard.display'),
  },
  monitoring: {
    title: t('hooks:monitoring'),
    description: t(
      'hooks:provides.full.stack.system.and.business.metric.collection.display.and.alerting.capabilities.covering.visualization.dashboards.custom.queries.intelligent.alerts.and.multi.source.integration.helping.users.fully.understand.system.operation.status.and.respond.to.abnormal.events.in.a.timely.manner',
    ),
  },
  monitoringOverview: {
    title: t('hooks:overview'),
    description: t('hooks:summary.display.of.system.alerts.operation.status.and.trends'),
  },
  visualization: {
    title: t('hooks:visualization.dashboard'),
    description: t('hooks:customizable.monitoring.panels.adapted.to.business.characteristics.and.scenario.requirements'),
  },
  metricsExplorer: {
    title: t('hooks:metric.query'),
    description: t('hooks:supports.metric.query.with.tag.and.dimension.filtering.and.time.series.aggregation.analysis'),
  },
  aiAlert: {
    title: t('hooks:intelligent.alerting'),
    description: t('hooks:supports.intelligent.alerting.and.self.healing.mechanisms.combining.rules.and.large.models'),
  },
  alertLogs: {
    title: t('hooks:alert.records'),
    description: DESC_ALERT_HISTORY,
  },
  monitorAlertLogs: {
    title: t('hooks:alert.records'),
    description: DESC_ALERT_HISTORY,
  },
  monitoringIntegration: {
    title: t('hooks:monitoring.integration'),
    description: t('hooks:supports.integration.with.mainstream.monitoring.data.sources.such.as.prometheus'),
  },
  logCenter: {
    title: t('hooks:log.center'),
    description: t(
      'hooks:unified.management.of.various.log.data.on.the.platform.supporting.efficient.log.collection.storage.query.and.alerting.adapts.to.structured.and.unstructured.log.scenarios.assisting.users.in.troubleshooting.behavior.auditing.and.intelligent.analysis',
    ),
  },
  logOverview: {
    title: t('hooks:overview'),
    description: t('hooks:quick.insight.into.log.alert.summaries.and.operational.trends'),
  },
  logSearch: {
    title: t('hooks:log.query'),
    description: t('hooks:provides.full.text.search.structured.filtering.and.sql.query.analysis.capabilities'),
  },
  logIngestion: {
    title: t('hooks:log.collection'),
    description: t('hooks:configure.multi.source.log.collection.rules.and.structure.definitions'),
  },
  logAIAlert: {
    title: t('hooks:intelligent.alerting'),
    description: t('hooks:implement.anomaly.detection.and.early.warning.based.on.log.rules.or.ai.models'),
  },
  logAlertLogs: {
    title: t('hooks:alert.records'),
    description: t('hooks:supports.archiving.auditing.and.querying.of.log.alerts'),
  },
  notificationSettings: {
    title: t('hooks:alarm.settings'),
    description: DESC_NOTIFY_CHANNELS,
  },
  notificationChannels: {
    title: t('alert:alert-medium-pl'),
    description: DESC_NOTIFY_CHANNELS,
  },
  systemImages: {
    title: t('common:system.image'),
    description: t(
      'hooks:provides.one.stop.automated.and.highly.reliable.system.image.creation.hosting.and.distribution.services.supports.multiple.computing.forms.such.as.cloud.servers.and.bare.metal.ensuring.fast.and.consistent.deployment.of.business.environments',
    ),
  },
  containerImages: {
    title: t('hooks:container.image'),
    description: t('hooks:supports.multi.architecture.and.multi.version.container.image.repository.management'),
  },
  systemImagesNew: {
    title: t('common:system.image'),
    description: t('hooks:system.image.repository.that.provides.system.image.listing.download.and.management.capabilities'),
  },
  drivers: {
    title: t('data-center:driver.program'),
    description: t('hooks:image.stack-artifact.driver.desc'),
  },
  runtime: {
    title: t('hooks:image.stack-artifact.runtime'),
    description: t('hooks:image.stack-artifact.runtime.desc'),
  },
  tool: {
    title: t('hooks:image.stack-artifact.tool'),
    description: t('hooks:image.stack-artifact.tool.desc'),
  },
  inferenceFramework: {
    title: t('hooks:image.stack-artifact.inference_framework'),
    description: t('hooks:image.stack-artifact.inference_framework.desc'),
  },
  modelRepos: {
    title: t('hooks:model.repository'),
    description: t('hooks:model.repository.menu.desc'),
  },
  datasetRepos: {
    title: t('hooks:dataset.repository'),
    description: t('hooks:dataset.repository.menu.desc'),
  },
  dataAssets: {
    title: t('hooks:data.assets'),
    description: t('hooks:data.assets.desc'),
  },
  modelPlaza: {
    title: t('home:market.model.repository'),
    description: t('home:market.model.repository.description'),
  },
  appPlaza: {
    title: t('home:market.app.center'),
    description: t('home:market.app.center.description'),
  },
  skillPlaza: {
    title: t('home:market.skill'),
    description: t('home:market.skill.description'),
  },
  imageProject: {
    title: t('hooks:image.project'),
  },
  imageList: {
    title: t('common:image.list'),
  },
  hpcNetwork: {
    title: t('hooks:computing.network'),
    description: t('hooks:computing.network.resource.management'),
  },
  hpcNetworkManagement: {
    title: t('hooks:computing.network.management'),
    description: t(
      'hooks:provides.unified.network.configuration.and.management.capabilities.for.ai.computing.resources.supports.multiple.network.forms.such.as.bare.metal.public.network.cloud.server.private.network.elastic.public.ip.etc.realizing.fine.grained.control.automatic.allocation.and.security.isolation.of.network.resources',
    ),
  },
  infraNetwork: {
    title: t('hooks:basic.network'),
    description: t(
      'hooks:provides.basic.network.capabilities.required.for.cloud.platform.operation.including.external.network.access.private.isolated.network.construction.and.public.ip.resource.management.meets.network.policy.configuration.and.communication.needs.in.multi.tenant.environments',
    ),
  },
  networkSecurity: {
    title: t('hooks:network.security'),
    description: t(
      'hooks:provides.virtualized.network.services.based.on.sdn.technology.and.multi.layer.security.protection.strategies.builds.an.isolated.high.speed.and.secure.network.environment.to.support.stable.interconnection.between.computing.instances.and.external.access',
    ),
  },
  externalNetwork: {
    title: t('hooks:elastic.ip.public.network.management'),
    description: t(
      'hooks:provides.application.binding.and.release.functions.for.elastic.public.ips.which.can.be.dynamically.bound.to.resources.such.as.cloud.servers.containers.and.gpu.nodes.realizing.flexible.public.network.access.capabilities',
    ),
  },
  vpc: {
    title: 'VPC',
    description: t(
      'hooks:provides.you.with.an.exclusively.logically.isolated.virtual.network.environment.on.the.cloud.the.system.automatically.assigns.ip.addresses.and.subnet.planning.ensuring.secure.isolation.and.stable.connections.between.computing.instances.users.only.need.to.focus.on.instance.deployment.without.caring.about.underlying.network.configuration',
    ),
  },
  securityGroup: {
    title: t('common:security.group'),
    description: t(
      'hooks:based.on.the.stateful.inspection.mechanism.of.virtual.firewalls.sets.network.access.control.rules.for.one.or.more.computing.instances.realizing.fine.grained.and.multi.dimensional.security.protection.for.instance.traffic',
    ),
  },
  loadBalance: {
    title: t('hooks:load.balancing'),
    description: t(
      'hooks:provides.flexible.load.balancing.strategies.supporting.multiple.algorithms.and.health.check.mechanisms.to.ensure.high.availability.and.stability.of.services',
    ),
  },
  L4LoadBalance: {
    title: t('hooks:layer.4.load.balancing'),
    description: t(
      'hooks:provides.flexible.layer.4.l4.load.balancing.strategies.supporting.multiple.algorithms.and.health.check.mechanisms.to.ensure.high.availability.and.stability.of.services',
    ),
  },
  policy: {
    title: t('common:strategy'),
    description: t(
      'hooks:provides.flexible.strategy.management.capabilities.supporting.the.creation.modification.and.deletion.of.multiple.strategies.to.ensure.high.availability.and.stability.of.services',
    ),
  },
  eip: {
    title: t('hooks:elastic.ip'),
    description: t(
      'hooks:supports.independently.applied.and.bound.public.ip.addresses.which.can.be.flexibly.bound.to.and.unbound.from.multiple.instances.such.as.cloud.servers.and.bare.metal.enables.fast.migration.of.public.network.services.and.allocation.binding.of.highly.available.ips.for.external.network.access.to.cloud.servers.or.container.services',
    ),
  },
  smartNet: {
    title: t('hooks:intelligent.network'),
    description: t(
      'hooks:for.ai.hpc.high.performance.computing.scenarios.builds.intelligent.network.services.with.low.latency.and.high.bandwidth.supports.new.communication.modes.such.as.rdma.optimizing.the.communication.efficiency.of.large.scale.distributed.tasks',
    ),
  },
  erdma: {
    title: t('cluster:elasticrdma'),
    description: t(
      'hooks:provides.elastically.schedulable.rdma.network.card.resources.supporting.roce.v2.infiniband.for.scenarios.such.as.ai.training.and.distributed.computing',
    ),
  },
  cmpResource: {
    title: t('hooks:computing.resource.management'),
    description: '',
  },
  hpcEngine: {
    title: t('hooks:computing.engine'),
    description: t('hooks:unified.management.platform.for.high.performance.computing.resources'),
  },
  cluster: {
    title: t('hooks:cluster.center'),
    description: t('hooks:cluster.center'),
  },
  dataCenterManagement: {
    title: t('common:datacenter'),
    description: t('hooks:system.menu.datacenter.desc'),
  },
  clusters: {
    title: t('hooks:computing.cluster.management'),
    description: t(
      'hooks:provides.unified.management.and.view.management.of.multiple.types.of.computing.clusters.within.the.platform.supports.configuration.scheduling.and.monitoring.of.master.virtualization.and.container.clusters.serving.as.the.basic.management.entry.for.computing.power.resources',
    ),
  },
  masterClusters: {
    title: t('hooks:cloud.management.cluster.management'),
    description: t('hooks:provides.unified.management.of.cloud.management.clusters'),
  },
  masterCluster: {
    title: t('hooks:cloud.management.cluster'),
    description: t('hooks:platform.core.management.and.control.cluster.unified.scheduling.and.configuration.entry'),
  },
  vmCluster: {
    title: t('hooks:cloud.server.cluster'),
    description: t('hooks:computing.cluster.bearing.standard.virtualized.computing.power.services'),
  },
  bizCluster: {
    title: t('hooks:biz.cluster'),
    description: t('hooks:business.scenario.oriented.kubernetes.computing.cluster'),
  },
  containerCluster: {
    title: t('hooks:computing.cluster'),
    description: t(
      'hooks:kubernetes.computing.cluster.for.deploying.business.workloads.supporting.task.scheduling.auto.scaling.and.application.deployment',
    ),
  },
  nodes: {
    title: t('hooks:computing.node.management'),
    description: t(
      'hooks:provides.unified.management.and.view.management.for.multiple.types.of.computing.nodes.within.the.platform.supporting.node.configuration.scheduling.and.monitoring.as.the.foundation.for.computing.resources',
    ),
  },
  computeResource: {
    title: t('product:deployment.category.compute'),
    description: t('hooks:resource.center.compute.resources.desc'),
  },
  schedulingResourcePool: {
    title: t('hooks:resource.center.scheduling.pool'),
    description: t('hooks:resource.center.scheduling.pool.desc'),
  },
  controlPanel: {
    title: t('hooks:resource.center.system.resources'),
    description: t('hooks:resource.center.system.resources.desc'),
  },
  networkPanel: {
    title: t('hooks:resource.center.network.resources'),
    description: t('hooks:resource.center.network.resources.desc'),
  },
  elasticCloudHost: {
    title: t('hooks:resource.center.cloud.host.resources'),
    description: t('hooks:resource.center.cloud.host.resources.desc'),
  },
  generalContainer: {
    title: t('hooks:resource.center.container.resources'),
    description: t('hooks:resource.center.container.resources.desc'),
  },
  highPerfContainer: {
    title: t('hooks:high.perf.container'),
    description: t('hooks:for.scenarios.such.as.ai.and.big.data.we.provide.container.nodes.with.gpu.or.dedicated.hardware.acceleration'),
  },
  elasticBareMetalInstance: {
    title: t('hooks:elastic.bare.metal'),
    description: t('hooks:elastic.bare.metal.instance.description'),
  },
  elasticBareMetalNode: {
    title: t('hooks:resource.center.bare.metal.resources'),
    description: t('hooks:resource.center.bare.metal.resources.desc'),
  },
  bareMetalPool: {
    title: t('hooks:resource.center.traditional.bare.metal.pool'),
    description: t('hooks:resource.center.traditional.bare.metal.pool.desc'),
  },
  assetManagement: {
    title: t('home:infrastructure'),
    description: t('hooks:resource.center.infrastructure.desc'),
  },
  networkAssets: {
    title: t('hooks:resource.center.network.egress'),
    description: t('hooks:resource.center.network.egress.desc'),
  },
  computeAssets: {
    title: t('hooks:resource.center.physical.nodes'),
    description: t('hooks:manage.rack.servers.and.bare.metal.raw.hardware'),
  },
  clusterAssets: {
    title: t('hooks:biz.cluster'),
    description: t('hooks:business.scenario.oriented.kubernetes.computing.cluster'),
  },
  storageAssets: {
    title: t('report:compute.storage.title'),
    description: t('hooks:manage.storage.types.such.as.local.disks.distributed.storage.parallel.file.systems.high.performance.ssds.etc'),
  },
  masterNodes: {
    title: t('hooks:cloud.management.node'),
    description: t('hooks:core.platform.control.node.unified.scheduling.and.configuration.entry.point'),
  },
  systemNodes: {
    title: t('hooks:cloud.management.system.node'),
    description: t('hooks:node.resource.label.for.deploying.cloud.management.system.services'),
  },
  networkNodes: {
    title: t('hooks:virtual.network.node'),
    description: t('hooks:node.resource.label.for.providing.virtual.network.related.services'),
  },
  cloudNodes: {
    title: t('hooks:cloud.host.node'),
    description: t('hooks:node.resource.label.for.providing.cloud.host.computing.instance.services'),
  },
  cloudGeneralNodes: {
    title: t('hooks:general.cloud.host.node'),
    description: t('hooks:node.resource.label.for.providing.general.cloud.host.computing.instance.services'),
  },
  cloudHpcNodes: {
    title: t('hooks:high.performance.cloud.host.node'),
    description: t('hooks:node.resource.label.for.providing.high.performance.cloud.host.computing.instance.services'),
  },
  containerNodes: {
    title: t('hooks:container.node'),
    description: t('hooks:node.resource.tags.for.providing.high.performance.container.cloud.computing.instance.services'),
  },
  containerGeneralNodes: {
    title: t('hooks:general.container.node'),
    description: t('hooks:node.resource.label.for.providing.general.container.computing.instance.services'),
  },
  containerHpcNodes: {
    title: t('hooks:high.performance.container.node'),
    description: t('hooks:node.resource.label.for.providing.high.performance.container.computing.instance.services'),
  },
  bareMetalNodes: {
    title: t('hooks:bare.metal.node'),
    description: t('hooks:node.resource.label.for.providing.high.performance.bare.metal.computing.instance.services'),
  },
  clusterService: {
    title: t('hooks:cluster.service'),
    description: t('hooks:provides.centralized.management.and.operational.support.for.kubernetes.clusters.and.their.core.components'),
  },
  bareMetal: {
    title: t('hooks:bare.metal.service'),
    description: t(
      'hooks:designed.for.task.scenarios.with.extremely.high.performance.and.isolation.requirements.provides.full.lifecycle.management.for.bare.metal.nodes.including.node.provisioning.resource.orchestration.and.high.performance.instance.delivery.supporting.native.computing.power.without.virtualization.layers',
    ),
  },
  bareMetalSubnet: {
    title: t('hooks:bare.metal.public.network.management'),
    description: t(
      'hooks:manages.public.network.access.capabilities.for.bare.metal.servers.including.allocation.binding.reclamation.and.policy.control.of.public.ip.addresses.ensuring.controllable.and.secure.external.network.communication.for.physical.nodes',
    ),
  },
  nodePool: {
    title: t('hooks:bare.metal.node'),
    description: t('hooks:group.and.manage.resources.for.bare.metal.server.nodes'),
  },
  bareMetalInstances: {
    title: t('hooks:instance.bare.metal'),
    description: t('hooks:high.performance.bare.metal.instances.for.hpc.model.training.tasks'),
  },
  bareMetalCloudDisk: {
    title: t('common:bare.metal.cloud.disk'),
    description: t('cloud-disk:bare-metal.description'),
  },
  cloudHostCloudDisk: {
    title: t('common:cloud.host.disk'),
    description: t(
      'hooks:provides.scalable.block.storage.services.for.virtualized.cloud.hosts.such.as.high.performance.computing.and.general.purpose.cloud.hosts.suitable.for.regular.computing.ai.model.deployment.log.storage.and.other.scenarios.with.high.availability.elasticity.and.snapshot.capabilities',
    ),
  },
  instances: {
    title: t('hooks:computing.instance'),
    description: t(
      'hooks:provides.various.forms.of.cloud.computing.power.services.including.high.performance.bare.metal.flexible.cloud.hosts.and.lightweight.containers.meeting.users.diverse.computing.needs.in.performance.isolation.elastic.scaling.etc',
    ),
  },
  generalInstances: {
    title: t('hooks:general.computing.instance'),
    description: t('hooks:standard.computing.resources.for.web.services.and.similar.scenarios'),
  },
  hpcInstances: {
    title: t('hooks:high.performance.computing.instance'),
    description: t('hooks:high.computing.power.instances.equipped.with.gpu.and.other.acceleration.resources'),
  },
  VMService: {
    title: t('cluster:cloud-host'),
    description: DESC_VM_COMPUTE,
  },
  VMInstances: {
    title: t('common:cloud.host'),
    description: DESC_VM_COMPUTE,
  },
  containerService: {
    title: t('hooks:container.service'),
    description: DESC_K8S_CONTAINER_PLATFORM,
  },
  containerInstances: {
    title: t('common:container'),
    description: DESC_K8S_CONTAINER_PLATFORM,
  },
  generalCtrInstances: {
    title: t('hooks:general.computing.instance'),
    description: t(
      'hooks:standard.container.computing.resources.for.web.services.lightweight.ai.inference.and.other.business.scenarios.providing.efficient.and.scalable.stateless.service.support',
    ),
  },
  hpcCtrInstances: {
    title: t('hooks:high.performance.computing.instance'),
    description: t(
      'hooks:containerized.computing.resources.equipped.with.gpu.and.high.performance.networking.meeting.the.dual.needs.of.performance.and.elastic.scheduling.for.high.computing.power.tasks.such.as.deep.learning.and.graph.computing',
    ),
  },
  cmpStorageManagement: {
    title: t('hooks:computing.storage.management'),
    description: t(
      'hooks:provides.unified.storage.resource.management.for.various.computing.tasks.covering.storage.type.configuration.capacity.planning.and.scheduling.strategies.supporting.multiple.high.performance.and.highly.reliable.storage.solutions.to.meet.the.needs.of.ai.training.big.data.processing.and.other.scenarios',
    ),
  },
  intelligentStorage: {
    title: t('hooks:intelligent.storage'),
    description: t(
      'hooks:high.performance.storage.service.for.ai.hpc.scenarios.providing.high.capacity.low.latency.storage.solutions.supporting.storage.management.and.data.access.in.hybrid.cloud.and.multi.cloud.environments',
    ),
  },
  persistentVolumes: {
    title: t('common:shared.storage.volume'),
    description: t(
      'hooks:supports.declarative.dynamic.storage.allocation.and.lifecycle.management.adapted.for.data.persistence.needs.of.container.cloud.host.tasks.suitable.for.storing.model.training.intermediate.results.logs.and.data.snapshots.compatible.with.multiple.backends',
    ),
  },
  dataCenterOverview: {
    title: t('hooks:overview'),
    description: t('hooks:overview.of.the.overall.status.of.the.data.center'),
  },
  computeCenterOverview: {
    title: t('hooks:overview'),
    description: t('hooks:compute.overview.description.compute'),
  },
  resourceCenterOverview: {
    title: t('hooks:overview'),
    description: t('hooks:compute.overview.description.resource'),
  },
  operationsGovernance: {
    title: t('hooks:operation.center'),
    description: t('hooks:system.menu.operations.management.desc'),
  },
  productManagement: {
    title: t('hooks:product.management'),
    description: t(
      'hooks:comprehensively.define.and.manage.the.specification.configuration.resource.binding.methods.and.billing.strategies.of.various.intelligent.computing.products.support.the.flexible.creation.of.multiple.types.of.products.such.as.computing.network.storage.and.operation.and.maintenance.and.provide.standardized.template.support.for.subsequent.resource.sales.service.delivery.and.fee.settlement',
    ),
  },
  /** 运营中心分组：轮播图、导航菜单、平台设置 */
  portalManagement: {
    title: t('hooks:portal.management'),
    description: t('hooks:portal.management.desc'),
  },
  /** 运营中心分组：通知管理、优惠策略 */
  operationStrategy: {
    title: t('hooks:operation.strategy'),
    description: t('hooks:system.menu.operation.strategy.desc'),
  },
  /** 运营中心分组：充值申请、运营配置 */
  rechargeManagement: {
    title: t('hooks:recharge.management'),
    description: t('hooks:system.menu.recharge.management.desc'),
  },
  carouselManagement: {
    title: t('hooks:carousel.management'),
    description: t('hooks:carousel.management.desc'),
  },
  productTypes: {
    title: t('hooks:product.category'),
    description: t('hooks:define.and.manage.the.specifications.and.resource.binding.of.various.intelligent.computing.products'),
  },
  productList: {
    title: t('common:product.list'),
    description: t('hooks:define.and.manage.the.specifications.and.resources.of.various.intelligent.computing.products'),
  },
  productSku: {
    title: t('common:product.specification'),
    description: t('hooks:define.and.manage.the.specifications.and.resources.of.various.intelligent.computing.products'),
  },
  billManagement: {
    title: t('hooks:billing.management'),
    description: t(
      'hooks:unify.the.setting.and.maintenance.of.billing.items.involved.in.various.intelligent.computing.products.covering.billing.models.pricing.strategies.and.settlement.rules.to.ensure.the.billing.process.is.clear.and.accurate.and.meet.the.billing.needs.of.diverse.business.scenarios',
    ),
  },
  billManagement1: {
    title: t('hooks:billing.management.2'),
    description: DESC_BILL_ACCOUNTING_SYSTEM,
  },
  billList: {
    title: t('common:bill.list'),
    description: DESC_BILL_ACCOUNTING_SYSTEM,
  },
  rechargeRecords: {
    title: t('common:recharge.records'),
    description: t(
      'hooks:record.the.inflow.time.amount.and.purpose.of.each.fund.provide.users.with.clear.fund.flow.traceability.and.balance.change.inquiry.and.serve.as.an.important.basis.for.financial.reconciliation.and.budget.management',
    ),
  },
  benefitsCenter: {
    title: t('hooks:benefits.center'),
    description: t('hooks:benefits.center.description'),
  },
  personalBenefitsCenter: {
    title: t('hooks:benefits.center'),
    description: t('hooks:benefits.center.personal.description'),
  },
  rechargeApply: {
    title: t('hooks:recharge.application'),
    description: t('hooks:system.menu.recharge.application.desc'),
  },
  billItems: {
    title: t('common:billing.item'),
    description: t('hooks:define.and.manage.the.billing.items.of.various.intelligent.computing.products'),
  },
  billModels: {
    title: t('common:billing.model'),
    description: t('hooks:define.and.manage.the.billing.models.of.various.intelligent.computing.products'),
  },
  resourceManagement: {
    title: t('hooks:resources'),
    description: t(
      'hooks:define.and.manage.the.atomic.resource.types.and.deployment.modes.required.by.various.intelligent.computing.products.covering.basic.resources.such.as.computing.storage.and.network.provide.unified.standards.for.product.configuration.and.resource.scheduling.and.support.flexible.allocation.and.efficient.utilization.of.resources',
    ),
  },
  resourcesAndDelivery: {
    title: t('hooks:resources.and.delivery'),
    description: t(
      'hooks:define.and.manage.the.atomic.resource.types.and.deployment.modes.required.by.various.intelligent.computing.products.covering.basic.resources.such.as.computing.storage.and.network.provide.unified.standards.for.product.configuration.and.resource.scheduling.and.support.flexible.allocation.and.efficient.utilization.of.resources',
    ),
  },
  tagManagement: {
    title: t('hooks:strategy.management'),
    description: t('hooks:define.and.manage.the.tags.of.various.intelligent.computing.products'),
  },
  atomResources: {
    title: t('common:atomic.resource'),
    description: t('hooks:define.and.manage.the.atomic.resources.of.various.intelligent.computing.products'),
  },
  deployTypes: {
    title: t('common:delivery.type'),
    description: t('hooks:define.and.manage.the.delivery.types.of.various.intelligent.computing.products'),
  },
  tags: {
    title: t('hooks:tag.management'),
    description: t('hooks:define.and.manage.the.tags.of.various.intelligent.computing.products'),
  },
  energyAnalyticsaim: {
    title: t('hooks:energy.efficiency.awareness'),
    description: t(
      'hooks:focus.on.the.energy.use.efficiency.of.data.centers.and.computing.resources.provide.multi.dimensional.power.consumption.statistics.trend.analysis.and.energy.saving.evaluation.and.assist.users.in.optimizing.resource.configuration.and.improving.energy.efficiency.levels',
    ),
  },
  situationAwareness: {
    title: t('hooks:situation.awareness'),
    description: t(
      'hooks:provide.real.time.monitoring.and.intelligent.analysis.capabilities.for.data.centers.and.computing.resources.helping.users.quickly.identify.potential.problems.and.optimization.suggestions',
    ),
  },
  clusterObservability: {
    title: t('hooks:cluster.awareness'),
    description: t(
      'hooks:provide.real.time.monitoring.and.performance.analysis.capabilities.for.computing.clusters.helping.users.quickly.identify.potential.problems.and.optimization.suggestions',
    ),
  },
  containerObservability: {
    title: t('hooks:container.awareness'),
    description: t(
      'hooks:provide.real.time.monitoring.and.performance.analysis.capabilities.for.containerized.applications.helping.users.quickly.identify.potential.problems.and.optimization.suggestions',
    ),
  },
  gpuObservability: {
    title: t('hooks:gpu.awareness'),
    description: t(
      'hooks:provides.real.time.monitoring.and.performance.analysis.for.gpu.resources.helping.users.quickly.identify.potential.issues.and.optimization.suggestions',
    ),
  },
  powerReport: {
    title: t('hooks:power.consumption.report'),
    description: t('hooks:time.period.and.device.specific.energy.consumption.statistics.and.energy.saving.optimization.suggestions'),
  },
  situationAwarenessDatasources: {
    title: t('hooks:data.source.management'),
    description: t('hooks:manage.data.source.configuration.and.integration.required.by.the.situation.awareness.module'),
  },
  IAM: {
    title: t('hooks:system.menu.organization.permissions'),
    description: t('hooks:system.menu.organization.permissions.desc'),
  },
  largeScreen: {
    title: t('common:data.dashboard'),
    description: t('hooks:system.menu.large.screen.desc'),
  },
  tenant: {
    title: t('hooks:tenant.management'),
    description: t('hooks:system.menu.tenant.desc'),
  },
  user: {
    title: t('cluster:user'),
    description: t('hooks:system.menu.user.desc'),
  },
  rbac: {
    title: t('hooks:role.management'),
    description: t('hooks:system.menu.role.desc'),
  },
  systemMenus: {
    title: t('hooks:system.menu.menu.configuration'),
    description: t('hooks:system.menu.menu.configuration.desc'),
  },
  loginSettings: {
    title: t('hooks:login.settings'),
    description: t('hooks:system.menu.platform.settings.desc'),
  },
  audit: {
    title: t('hooks:system.menu.operation.audit'),
    description: t('hooks:system.menu.operation.audit.desc'),
  },
  console: {
    title: t('common:tenant.space'),
    description: t(
      'hooks:provide.a.separate.management.and.resource.view.for.each.tenant.the.console.is.the.unified.entry.for.users.to.access.the.intelligent.computing.service',
    ),
  },
  consoleOverview: {
    title: t('hooks:overview'),
    description: t('hooks:visualization.of.tenant.resource.distribution.and.system.dynamics'),
  },
  members: {
    title: t('hooks:member.management'),
    description: t('hooks:tenant.user.list.and.status.management'),
  },
  clusterOverview: {
    title: t('hooks:overview'),
    description: t('hooks:cluster.overall.status.overview'),
  },
  clusterInfo: {
    title: t('cluster:cluster-information'),
    description: t('hooks:cluster.basic.configuration.and.resource.usage'),
  },
  clusterNodes: {
    title: t('common:cluster.nodes'),
    description: t('hooks:cluster.node.resource.management'),
  },
  clusterMonitoring: {
    title: t('common:cluster.monitoring'),
    description: t('hooks:cluster.operation.metrics.monitoring'),
  },
  namespaces: {
    title: t('common:namespace'),
    description: t('hooks:cluster.namespace.management'),
  },
  application: {
    title: t('cluster:applicationload'),
    description: t('hooks:application.workload.management'),
  },
  workloads: {
    title: t('common:workload'),
    description: t('hooks:application.workload.management'),
  },
  pods: {
    title: t('cluster:pods'),
    description: t('hooks:container.pod.instance.management'),
  },
  services: {
    title: t('common:service'),
    description: t('hooks:cluster.service.resource.management'),
  },
  configuration: {
    title: t('cluster:config'),
    description: t('hooks:cluster.configuration.resource.management'),
  },
  secrets: {
    title: t('common:secret'),
    description: t('hooks:cluster.sensitive.information.management'),
  },
  configMaps: {
    title: t('common:configmap'),
    description: t('hooks:cluster.configuration.information.management'),
  },
  storage: {
    title: t('common:storage'),
    description: t('hooks:cluster.storage.resource.management'),
  },
  pvc: {
    title: t('common:persistent.volume.claim'),
    description: t('hooks:persistent.storage.claim.management'),
  },
  pv: {
    title: t('common:persistent.volume'),
    description: t('hooks:persistent.storage.volume.management'),
  },
  storageClasses: {
    title: t('hooks:storage.type.management'),
    description: t(
      'hooks:unified.management.of.various.storage.types.supported.by.the.platform.such.as.local.disks.distributed.storage.parallel.file.systems.high.performance.ssds.etc.with.support.for.configuring.their.performance.usage.and.scheduling.policies',
    ),
  },
  clusterSettings: {
    title: t('cluster:cluster-setting'),
    description: t('hooks:cluster.basic.configuration.and.resource.management'),
  },
  dataCenterMembers: {
    title: t('hooks:member.management'),
    description: t('hooks:data.center.user.member.lifecycle.management.and.authorization.policies'),
  },
  dataCenterRBAC: {
    title: t('hooks:role.management'),
    description: DESC_RBAC_POLICY,
  },
  clusterMembers: {
    title: t('hooks:member.management'),
    description: t('hooks:cluster.user.permission.management'),
  },
  clusterRBAC: {
    title: t('hooks:role.management'),
    description: DESC_RBAC_POLICY,
  },
  // 报表中心
  reportCenter: {
    title: t('hooks:report.center'),
    description: t('hooks:report.center.desc'),
  },
  reportOverview: {
    title: t('hooks:report.overview'),
    description: t('hooks:report.overview.desc'),
  },
  reportCompute: {
    title: t('hooks:report.compute'),
    description: t('hooks:report.compute.desc'),
  },
  reportModel: {
    title: t('hooks:report.model'),
    description: t('hooks:report.model.desc'),
  },
  reportApp: {
    title: t('hooks:report.app'),
    description: t('hooks:report.app.desc'),
  },
  reportOps: {
    title: t('hooks:report.ops'),
    description: t('hooks:report.ops.desc'),
  },
  reportBusiness: {
    title: t('hooks:report.business'),
    description: t('hooks:report.business.desc'),
  },
  reportResource: {
    title: t('hooks:report.resource'),
    description: t('hooks:report.resource.desc'),
  },
  systemSettingsMode: {
    title: t('hooks:system'),
    description: t(
      'hooks:provides.a.centralized.management.portal.for.system.administrators.supporting.user.permission.configuration.system.parameter.settings.and.unified.management.of.platform.core.capabilities',
    ),
  },
  dataCenterMode: {
    title: t('hooks:data.center'),
    description: t(
      'hooks:provide.a.comprehensive.infrastructure.and.resource.management.capability.for.the.data.center.covering.computing.power.network.storage.image.monitoring.and.permissions',
    ),
  },
  tenantMode: {
    title: t('hooks:tenant.management'),
    description: t(
      'hooks:provides.a.unified.resource.management.and.service.operation.entry.for.different.tenants.covering.core.functional.modules.such.as.computing.power.network.storage.images.and.permissions',
    ),
  },
  personal: {
    title: t('common:personal.center'),
    description: t('common:personal.center'),
  },
  password: {
    title: t('hooks:change.password'),
    description: t('hooks:change.password'),
  },
  auth: {
    title: t('hooks:view.permissions'),
    description: t('hooks:view.current.user.permissions'),
  },
  productCatalogue: {
    // 原「产品目录」，菜单与权限展示统一为导航菜单
    title: t('hooks:system.menu.navigation.menu'),
    description: t('hooks:system.menu.product.navigation.desc'),
  },
  orderManagement: {
    title: t('hooks:order.management'),
    description: DESC_ORDER_WORKBENCH,
  },
  orderList: {
    title: t('common:order.list'),
    description: DESC_ORDER_WORKBENCH,
  },
  // 费用中心
  chargeCenter: {
    title: t('hooks:billing.center'),
    description: t(
      'hooks:provides.users.with.one.stop.transparent.financial.supervision.and.efficient.cost.control.services.covering.all.financial.aspects.from.order.management.with.payment.and.status.tracking.to.bill.management.with.detailed.query.and.download.to.recharge.records.of.fund.flows.and.balance.changes',
    ),
  },
  sysAccount: {
    title: t('hooks:system.menu.operations.configuration'),
    description: t('hooks:system.menu.operations.configuration.desc'),
  },
  chargeOverview: {
    title: t('hooks:transaction.details'),
    description: t('hooks:analyze.details.to.understand.business.drivers.overview.the.whole.to.control.the.profit.landscape'),
  },
  productsCatalogue: {
    title: t('hooks:artifact.center'),
    description: t(
      'hooks:centrally.manage.and.display.all.your.software.artifacts.including.images.code.packages.documents.etc.building.a.traceable.and.reusable.software.asset.repository',
    ),
  },

  noticeList: {
    title: t('hooks:system.menu.notification.management'),
    description: t('hooks:system.menu.notification.management.desc'),
  },
  couponStrategy: {
    title: t('hooks:coupon.strategy'),
    description: t('hooks:system.menu.coupon.strategy.desc'),
  },
  tenantSetting: {
    title: t('hooks:workspace.settings'),
    description: t('hooks:tenant.setting.management'),
  },
  // 全局视图
  globalView: {
    title: t('hooks:global.view'),
    description: t('hooks:global.view.management'),
  },
  // 资源管理
  systemResource: {
    title: t('hooks:resources'),
    description: t('hooks:system.menu.datacenter.desc'),
  },
  systemSettings: {
    title: t('order:system.configuration'),
    description: t('hooks:system.menu.system.configuration.desc'),
  },
  networkDevice: {
    title: t('hooks:network.device'),
    description: t('hooks:network.device.management'),
  },
  hardwareAssets: {
    title: t('data-center:hardware.resource'),
    description: t('hardware:menu.list.desc'),
  },
  computeCenter: {
    title: t('hooks:computing.power.center'),
    description: t('hooks:computing.power.center.desc'),
  },
  resourceCenter: {
    title: t('hooks:resource.center'),
    description: t('hooks:manage.infrastructure.assets.such.as.networks.computing.clusters.and.storage'),
  },
  aiCenter: {
    title: t('hooks:model.center'),
    description: t('hooks:model.center.desc'),
  },
  aiOverview: {
    title: t('hooks:overview'),
    description: t('hooks:ai.center.overview.desc'),
  },
  appInstance: {
    title: t('hooks:app.instance'),
    description: t('hooks:app.instance.desc'),
  },
  modelInstance: {
    title: t('data-center:model.instance.menu'),
    description: t('hooks:model.instance.desc'),
  },

  modelSubscription: {
    title: t('hooks:model.subscription'),
    description: t('hooks:model.subscription.desc'),
  },
  modelAuditLog: {
    title: t('hooks:model.audit.log'),
    description: t('hooks:model.audit.log.desc'),
  },
  modelProviderManagement: {
    title: t('hooks:model.provider.management'),
    description: t('hooks:model.provider.management.desc'),
  },
  modelDesensitization: {
    title: t('hooks:model.desensitization'),
    description: t('hooks:model.desensitization.desc'),
  },
  apiKeyManagement: {
    title: t('hooks:model.api.key.management'),
    description: t('hooks:model.api.key.management.desc'),
  },
  aiApplications: {
    title: t('hooks:ai.app'),
    description: t('hooks:ai.app.desc'),
  },
  skills: {
    title: t('hooks:skills'),
    description: t('hooks:skills.desc'),
  },
  modelExperience: {
    title: t('hooks:model.experience'),
    description: t('hooks:model.experience.desc'),
  },
  textModelExperience: {
    title: t('hooks:text.model'),
    description: t('hooks:text.model.desc'),
  },
  audioModelExperience: {
    title: t('hooks:audio.model'),
    description: t('hooks:audio.model.desc'),
  },
  visionModelExperience: {
    title: t('hooks:vision.model'),
    description: t('hooks:vision.model.desc'),
  },
  embeddingModelExperience: {
    title: t('hooks:embedding.model'),
    description: t('hooks:embedding.model.desc'),
  },
  modelCenter: {
    title: t('hooks:model.center'),
    description: t('hooks:model.center.desc'),
  },
  modelService: {
    title: t('data-center:model.subscription.step.model.service'),
  },
  modelTraining: {
    title: t('data-center:training.model.title'),
  },
  trainingCluster: {
    title: t('data-center:training.task.cluster'),
    description: t('data-center:training.cluster.description'),
  },
  trainingTask: {
    title: t('data-center:training.task.title'),
    description: t('data-center:training.task.description'),
  },
  securityGovernance: {
    title: t('hooks:security.governance'),
  },
  appCenter: {
    title: t('hooks:app.center'),
    description: t('hooks:app.center.desc'),
  },
  applicationMarket: {
    title: t('home:market.application'),
  },
  instanceManagement: {
    title: t('hooks:instance.management'),
  },
} as const;

/** NAV_MAP 的推断类型，供 `keyof typeof NAV_MAP`、`NavMap[...]` 等使用 */
export type NavMap = typeof NAV_MAP;
